/**
 * A discount code that arrived in a link: `?code=SAVE10` on any page.
 *
 * Sellers share their store or a product with the code in the URL (the Copy
 * link button on the dashboard's Codes tab builds one). The layout captures it
 * here on every page load and navigation, and checkout applies it to the first
 * quote, so the buyer never has to type it. The cart says it will be applied.
 *
 * sessionStorage, not localStorage, for the reason checkout-attribution.ts
 * gives: the code came with this tab's visit, and should die with the tab
 * rather than turn up on an unrelated purchase days later. The TTL covers a tab
 * left open overnight. Only the normalised code is stored, and only one: a
 * later link replaces an earlier one.
 *
 * Advisory in the same sense as the attribution: the server decides whether a
 * code applies, and a stale or forged value can at worst be refused.
 */

import {
  browserSessionStorage, readStorageItem, removeStorageItems, writeStorageItem,
  type KeyValueStorage,
} from './browser-storage.ts';
import { normalizeDiscountCode } from './discount-codes.ts';

/** The query parameter a shared link carries the code in. */
export const DISCOUNT_CODE_PARAM = 'code';
export const LINK_CODE_STORAGE_KEY = 'discountCode';
export const LINK_CODE_TTL_MS = 24 * 60 * 60 * 1000;

type Options = { storage?: KeyValueStorage | null; now?: number };

const storageFor = (options: Options) =>
  options.storage === undefined ? browserSessionStorage() : options.storage;

/** The code in a query string ("?code=save10" gives "SAVE10"), or null. */
export function discountCodeFromSearch(search: string | null | undefined): string | null {
  if (!search) return null;
  try {
    return normalizeDiscountCode(new URLSearchParams(search).get(DISCOUNT_CODE_PARAM));
  } catch {
    return null;
  }
}

/** Keep a code from a link for this tab. Best effort: never throws. */
export function rememberLinkDiscountCode(code: string, options: Options = {}): void {
  const clean = normalizeDiscountCode(code);
  if (!clean) return;
  writeStorageItem(storageFor(options), LINK_CODE_STORAGE_KEY,
    JSON.stringify({ code: clean, at: options.now ?? Date.now() }));
}

/**
 * The code a link left for this tab, or null. A stale or malformed entry is
 * removed as it is found.
 */
export function readLinkDiscountCode(options: Options = {}): string | null {
  const storage = storageFor(options);
  const stored = readStorageItem(storage, LINK_CODE_STORAGE_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as { code?: unknown; at?: unknown } | null;
    const code = normalizeDiscountCode(parsed?.code);
    const at = Number(parsed?.at);
    const now = options.now ?? Date.now();
    if (code && Number.isFinite(at) && now - at <= LINK_CODE_TTL_MS && at - now <= 60_000) return code;
  } catch {
    /* malformed: removed below */
  }
  removeStorageItems(storage, LINK_CODE_STORAGE_KEY);
  return null;
}

/** Drop the link's code: the buyer removed it at checkout. */
export function forgetLinkDiscountCode(options: Options = {}): void {
  removeStorageItems(storageFor(options), LINK_CODE_STORAGE_KEY);
}

/** Capture the code from a page's query string, if it carries one. Returns what was kept. */
export function captureLinkDiscountCode(search: string | null | undefined, options: Options = {}): string | null {
  const code = discountCodeFromSearch(search);
  if (code) rememberLinkDiscountCode(code, options);
  return code;
}
